/**
 * DistributionTypeFilter - Filtro de tipo de conversa para dashboard de distribuição
 * 
 * Permite filtrar métricas e distribuições por:
 * - Todas as conversas
 * - Apenas individuais
 * - Apenas grupos
 * 
 * Design premium com tabs estilizados e contadores
 * 
 * @version 1.0.0
 * @date 2026-01-07
 */

import { MessageSquare, User, Users } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

// ==============================================================
// Tipos
// ==============================================================

/** Tipos de filtro disponíveis */
export type DistributionTypeFilterValue = 'all' | 'individual' | 'group';

// ==============================================================
// Interfaces
// ==============================================================

interface DistributionTypeFilterProps {
  /** Valor atual do filtro */
  value: DistributionTypeFilterValue;
  /** Callback quando o filtro muda */
  onChange: (value: DistributionTypeFilterValue) => void;
  /** Contadores por tipo */
  counts: {
    all: number;
    individual: number;
    group: number;
  };
  /** Indica se está carregando */
  isLoading?: boolean;
}

// ==============================================================
// Componente Principal
// ==============================================================

export function DistributionTypeFilter({ 
  value, 
  onChange, 
  counts, 
  isLoading 
}: DistributionTypeFilterProps) {
  // Configuração dos botões de filtro
  const filterOptions: Array<{
    key: DistributionTypeFilterValue;
    label: string;
    icon: typeof MessageSquare;
    color: string;
  }> = [
    { key: 'all', label: 'Todas', icon: MessageSquare, color: 'text-foreground' },
    { key: 'individual', label: 'Individuais', icon: User, color: 'text-emerald-600' },
    { key: 'group', label: 'Grupos', icon: Users, color: 'text-blue-600' },
  ];

  return (
    <div className="flex gap-1.5 p-1 bg-muted/50 rounded-lg w-fit">
      {filterOptions.map(option => {
        const Icon = option.icon;
        const isActive = value === option.key;
        const count = counts[option.key];
        
        return (
          <Button
            key={option.key}
            variant={isActive ? 'default' : 'ghost'}
            size="sm"
            onClick={() => onChange(option.key)}
            disabled={isLoading}
            className={cn(
              'gap-1.5 h-8 px-3 transition-all duration-200',
              isActive && 'shadow-sm',
              !isActive && 'hover:bg-muted'
            )}
          >
            <Icon className={cn(
              'h-3.5 w-3.5',
              isActive ? 'text-primary-foreground' : option.color
            )} />
            <span className="text-xs font-medium">
              {option.label}
            </span>
            <span className={cn(
              'text-[10px] px-1.5 py-0.5 rounded-full min-w-[20px] text-center',
              isActive 
                ? 'bg-primary-foreground/20 text-primary-foreground' 
                : 'bg-muted text-muted-foreground'
            )}>
              {isLoading ? '-' : count}
            </span>
          </Button>
        );
      })}
    </div>
  );
}
